import React, { useEffect, useRef } from 'react';
import { useAds } from '../../contexts/AdContext';
import { useLanguage } from '../../contexts/LanguageContext';
import './ConsentDialog.css';

/**
 * Issue #268: Optimized ad consent popover
 * Shows after user has seen 5 user-generated contents
 * New design with "Deals anzeigen" button
 */
function ConsentDialog() {
  const { showConsentPopover, grantConsent, declineConsent, setShowConsentPopover } = useAds();
  const { t } = useLanguage();
  const dialogRef = useRef(null);
  const firstButtonRef = useRef(null);

  // Focus trap and initial focus
  useEffect(() => {
    if (showConsentPopover && firstButtonRef.current) {
      firstButtonRef.current.focus();
    }
  }, [showConsentPopover]);

  // Handle keyboard escape
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showConsentPopover) {
        declineConsent();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showConsentPopover, declineConsent]);

  if (!showConsentPopover) return null;

  return (
    <div
      className="consent-dialog-overlay"
      role="presentation"
    >
      <div
        className="consent-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="consent-dialog-title"
        aria-describedby="consent-dialog-description"
        ref={dialogRef}
      >
        {/* Header */}
        <h2 id="consent-dialog-title" className="consent-dialog-header">
          {t('ads.consent.welcome')}
        </h2>

        {/* Title */}
        <h3 className="consent-dialog-title">
          {t('ads.consent.title')}
        </h3>

        {/* Description */}
        <p id="consent-dialog-description" className="consent-dialog-description">
          {t('ads.consent.description')}
        </p>

        {/* Benefits list */}
        <p className="consent-dialog-benefits-intro">{t('ads.consent.benefitsIntro')}</p>
        <ul className="consent-dialog-benefits" aria-label={t('ads.consent.benefitsLabel')}>
          <li>{t('ads.consent.benefit1')}</li>
          <li>{t('ads.consent.benefit2')}</li>
          <li>{t('ads.consent.benefit3')}</li>
        </ul>

        {/* Changeable notice */}
        <p className="consent-dialog-notice">
          {t('ads.consent.changeableNotice')}
        </p>

        {/* Buttons */}
        <div className="consent-dialog-buttons">
          <button
            className="consent-dialog-button consent-decline"
            onClick={declineConsent}
            type="button"
            aria-label={t('ads.consent.declineAriaLabel')}
          >
            {t('ads.consent.decline')}
          </button>
          <button
            ref={firstButtonRef}
            className="consent-dialog-button consent-accept"
            onClick={grantConsent}
            type="button"
            aria-label={t('ads.consent.acceptAriaLabel')}
          >
            {t('ads.consent.accept')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ConsentDialog;
